/**
 * Parent-order asset mirror resolution.
 *
 * For multi-leg orders the `legs` rows are the authoritative asset
 * assignment; the parent `orders` row only carries a *mirror* of leg 1 for
 * back-compat consumers (see `.lovable/memory/features/orders/per-leg-trailer.md`).
 *
 * The edit form hydrates its order-level asset fields from the parent at load
 * time and never clears them when the user removes a truck per leg, so writing
 * the parent straight from those form fields leaves a stale mirror behind —
 * which then makes the truck render as "booked" in every other order's
 * dropdown. Always resolve the parent mirror through this helper.
 */

export interface LegAssetSource {
  assigned_tractor_id?: string | null;
  assigned_operator_id?: string | null;
  assigned_operator_2_id?: string | null;
  trailer_id?: string | null;
  trailer_type_id?: string | null;
}

export interface ParentAssetMirrorSource extends LegAssetSource {
  order_type?: string | null;
  use_same_assets_for_all_legs?: boolean;
  legs?: LegAssetSource[] | null;
}

export interface ParentAssetMirror {
  assigned_tractor_id: string | null;
  assigned_operator_id: string | null;
  assigned_operator_2_id: string | null;
  trailer_id: string | null;
  trailer_type_id: string | null;
}

type ParentAssetKey = keyof ParentAssetMirror;

const PARENT_ASSET_KEYS: ParentAssetKey[] = [
  "assigned_tractor_id",
  "assigned_operator_id",
  "assigned_operator_2_id",
  "trailer_id",
  "trailer_type_id",
];

const norm = (value?: string | null): string | null => {
  const trimmed = (value ?? "").trim();
  return trimmed === "" ? null : trimmed;
};

/**
 * Returns the asset fields to write on the parent `orders` row.
 *
 * - Per-leg multi-leg orders (`use_same_assets_for_all_legs === false`):
 *   mirror leg 1, including `null` when leg 1 has no assignment.
 * - Everything else (single-leg, or multi-leg with shared assets): use the
 *   order-level values as-is.
 */
export function resolveParentAssetMirror(
  data: ParentAssetMirrorSource,
): ParentAssetMirror {
  const isPerLegAssets =
    data.order_type === "multi-leg" &&
    data.use_same_assets_for_all_legs === false &&
    Array.isArray(data.legs) &&
    data.legs.length > 0;

  const source: LegAssetSource = isPerLegAssets ? data.legs![0] : data;

  return {
    assigned_tractor_id: norm(source.assigned_tractor_id),
    assigned_operator_id: norm(source.assigned_operator_id),
    assigned_operator_2_id: norm(source.assigned_operator_2_id),
    trailer_id: norm(source.trailer_id),
    trailer_type_id: norm(source.trailer_type_id),
  };
}

/**
 * Resolve the parent mirror for an edit without repairing stale parent data as
 * a side effect of an unrelated leg edit. For per-leg orders, a parent asset
 * is updated only when that asset changed on leg 1 compared with its persisted
 * snapshot. Otherwise the parent's current value is preserved so row locks do
 * not interpret mirror drift as a dispatcher-initiated asset change.
 */
export function resolveChangedParentAssetMirror(
  data: ParentAssetMirrorSource,
  currentParent: LegAssetSource,
  persistedFirstLeg?: LegAssetSource | null,
): ParentAssetMirror {
  const next = resolveParentAssetMirror(data);
  const isPerLegAssets =
    data.order_type === "multi-leg" &&
    data.use_same_assets_for_all_legs === false &&
    Array.isArray(data.legs) &&
    data.legs.length > 0 &&
    persistedFirstLeg;

  if (!isPerLegAssets) return next;

  const editedFirstLeg = data.legs?.[0];
  if (!editedFirstLeg) return next;

  const result = { ...next };
  for (const key of PARENT_ASSET_KEYS) {
    if (norm(editedFirstLeg[key]) === norm(persistedFirstLeg[key])) {
      result[key] = norm(currentParent[key]);
    }
  }
  return result;
}
